import React, { useState, useCallback } from 'react'
import { useQuery } from 'react-query'
import {
  BarChart3,
  Users,
  Globe,
  Eye,
  RefreshCw,
  Download,
  ChevronDown,
  Activity,
  MonitorSpeaker,
  Smartphone,
  Tablet,
  FileEdit,
  Rocket,
  AlertCircle,
  TrendingUp,
  Link2,
} from 'lucide-react'
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid,
  ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'

const SITE_STATUS_COLORS = {
  Published: '#6c63ff',
  Draft: '#a3a3a3',
}

// ── Stat Card ──────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, subtitle, index = 0, isFetching, isLoading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.06 }}
  >
    <Card className={`hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="space-y-0.5 min-w-0">
            <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400 truncate">{title}</p>
            {isLoading ? (
              <div className="animate-pulse bg-neutral-300 dark:bg-neutral-600 h-7 w-16 rounded" />
            ) : (
              <p className="text-xl font-bold text-neutral-900 dark:text-neutral-50">
                {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
              </p>
            )}
            {subtitle && !isLoading && (
              <p className="text-xs text-neutral-400 dark:text-neutral-500 truncate">{subtitle}</p>
            )}
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg shrink-0">
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
)

// ── Activity Chart (edits + publishes + page views) ───────────────────────
const ActivityChart = React.memo(({ data }) => {
    const [tooltip, setTooltip] = useState(null)
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
    const hasViews = data?.some(d => d.views > 0)

    const handleMouseMove = useCallback((e) => {
      if (e?.activePayload?.length) setTooltip(e.activePayload)
    }, [])
    const handleMouseMoveCapture = useCallback((e) => {
      const wrapper = e.currentTarget.querySelector('.recharts-wrapper')
      if (wrapper) {
        const rect = wrapper.getBoundingClientRect()
        setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
      }
    }, [])

    if (!data?.length) {
      return <div className="h-64 flex items-center justify-center text-neutral-400 dark:text-neutral-500 text-sm">No activity recorded in this period</div>
    }

    return (
      <div className="relative" onMouseMove={handleMouseMoveCapture} onMouseLeave={() => setTooltip(null)}>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
            <defs>
              <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.6} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="colorEdits" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6c63ff" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#6c63ff" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="colorPublishes" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-neutral-200 dark:text-neutral-700" />
            <XAxis dataKey="name" tick={{ fill: 'currentColor', fontSize: 10 }} className="text-neutral-500 dark:text-neutral-400" interval="preserveStartEnd" />
            <YAxis tick={{ fill: 'currentColor', fontSize: 10 }} className="text-neutral-500 dark:text-neutral-400" allowDecimals={false} width={32} />
            {hasViews && <Area type="monotone" dataKey="views" stroke="#f59e0b" fill="url(#colorViews)" strokeWidth={2} name="Page Views" />}
            <Area type="monotone" dataKey="edits" stroke="#6c63ff" fill="url(#colorEdits)" strokeWidth={2} name="Edits" />
            <Area type="monotone" dataKey="publishes" stroke="#10b981" fill="url(#colorPublishes)" strokeWidth={2} name="Publishes" />
          </AreaChart>
        </ResponsiveContainer>
        {tooltip && (
          <div className="absolute z-50 bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 shadow-lg pointer-events-none"
            style={{ left: mousePos.x + 12, top: mousePos.y - 10, transform: mousePos.x > 400 ? 'translateX(-110%)' : 'none' }}>
            {tooltip.map((entry, i) => (
              <p key={i} className="text-sm flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-neutral-500 dark:text-neutral-400">{entry.name}:</span>
                <span className="font-semibold" style={{ color: entry.color }}>{entry.value.toLocaleString()}</span>
              </p>
            ))}
          </div>
        )}
        <div className="flex items-center gap-4 mt-2 justify-center flex-wrap">
          {hasViews && <span className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" /> Page Views</span>}
          <span className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400"><span className="w-3 h-3 rounded-full bg-accent-600 inline-block" /> Edits</span>
          <span className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400"><span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> Publishes</span>
        </div>
      </div>
    )
  })

// ── Device / Status Chart ──────────────────────────────────────────────────
const DeviceOrStatusChart = React.memo(({ deviceTypes, published, draft }) => {
    const hasDeviceData = deviceTypes?.length > 0
    const data = hasDeviceData
      ? deviceTypes
      : [
          { name: 'Published', value: published, color: SITE_STATUS_COLORS.Published },
          { name: 'Draft', value: draft, color: SITE_STATUS_COLORS.Draft },
        ].filter(d => d.value > 0)

    const deviceIcons = { Desktop: MonitorSpeaker, Mobile: Smartphone, Tablet: Tablet }

    if (!data.length) return <div className="h-40 flex items-center justify-center text-neutral-400 dark:text-neutral-500 text-sm">No data yet</div>

    return (
      <div className="flex flex-col lg:flex-row items-center gap-6">
        <ResponsiveContainer width={160} height={160}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={4} dataKey="value" stroke="none">
              {data.map((entry, i) => <Cell key={i} fill={entry.color} stroke="none" />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-2 w-full">
          {data.map((entry, i) => {
            const Icon = deviceIcons[entry.name] || Globe
            return (
              <div key={i} className="flex items-center justify-between p-2.5 bg-white dark:bg-[#2f2b41] rounded-lg shadow-sm">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: entry.color + '22' }}>
                    <Icon className="h-4 w-4" style={{ color: entry.color }} />
                  </div>
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{entry.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-base font-bold text-neutral-900 dark:text-neutral-100">{entry.value}{hasDeviceData ? '%' : ''}</div>
                  {hasDeviceData && <div className="text-xs text-neutral-500">{entry.count?.toLocaleString()} visits</div>}
                  {!hasDeviceData && <div className="text-xs text-neutral-500">sites</div>}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  })

const AnalyticsDashboard = () => {
  const [dateRange, setDateRange] = useState('30d')

  const { data: analytics, isLoading, isFetching, isError, refetch } = useQuery(
    ['analytics', dateRange],
    () => apiService.get(`/analytics/admin/overview?range=${dateRange}`),
    {
      staleTime: 120000,
      refetchInterval: 600000,
      retry: 2,
      keepPreviousData: true,
    }
  )

  const displayData = analytics?.data || analytics

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!displayData) return
    const { overview, activityData = [], activeSites = [], recentPublishes = [], topPages = [], referrerData = [], siteTraffic = [] } = displayData

    const rows = [
      ['WebNest Analytics Export', `Range: ${dateRange}`, `Generated: ${new Date().toLocaleString()}`],
      [],
      ['=== OVERVIEW ==='],
      ['Metric', 'Value'],
      ['Published Sites', overview?.publishedSites ?? 0],
      ['Draft Sites', overview?.draftSites ?? 0],
      ['Total Sites', overview?.totalSites ?? 0],
      ['Total Clients', overview?.totalClients ?? 0],
      ['Page Views', overview?.totalPageViews ?? 0],
      ['Unique Visitors', overview?.uniqueVisitors ?? 0],
      ['Edits in Period', overview?.editsInPeriod ?? 0],
      ['Publishes in Period', overview?.publishesInPeriod ?? 0],
      [],
      ['=== ACTIVITY OVER TIME ==='],
      ['Period', 'Edits', 'Publishes', 'Page Views'],
      ...activityData.map(r => [r.name, r.edits, r.publishes, r.views ?? 0]),
      [],
      ['=== TRAFFIC SOURCES ==='],
      ['Source', 'Visits', '% of Total'],
      ...referrerData.map(r => [r.source, r.count, r.percentage + '%']),
      [],
      ['=== TRAFFIC BY SITE ==='],
      ['Site', 'Client', 'Views', 'Unique Visitors'],
      ...siteTraffic.map(s => [s.name, s.company_name, s.views, s.unique_visitors]),
      [],
      ['=== TOP PAGES ==='],
      ['Page', 'Views', '% of Total'],
      ...topPages.map(p => [p.page, p.views, p.percentage + '%']),
      [],
      ['=== MOST ACTIVE SITES ==='],
      ['Site', 'Client', 'Edits'],
      ...activeSites.map(s => [s.name, s.company_name, s.editCount]),
      [],
      ['=== RECENTLY PUBLISHED ==='],
      ['Site', 'Client', 'Published At'],
      ...recentPublishes.map(s => [s.name, s.company_name, new Date(s.published_at).toLocaleString()]),
    ]

    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `webnest-analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Loading (first load only) ──────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading analytics..." />
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (isError || !displayData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-neutral-500 dark:text-neutral-400">
        <AlertCircle className="h-8 w-8 text-error-500" />
        <p className="text-sm">Failed to load analytics. Check your connection and try again.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-3.5 w-3.5 mr-2" /> Retry
        </Button>
      </div>
    )
  }

  const { overview, activityData = [], activeSites = [], recentPublishes = [], topPages = [], deviceTypes = [], referrerData = [], siteTraffic = [] } = displayData

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8 pt-4">

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
            Analytics
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
            {isFetching
              ? <><RefreshCw className="h-3 w-3 animate-spin" /> Updating…</>
              : <>Platform activity · last updated {new Date(displayData.lastUpdated).toLocaleTimeString()}</>
            }
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="h-9 pl-3 pr-9 bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-600 text-neutral-900 dark:text-neutral-100 appearance-none cursor-pointer"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400 pointer-events-none" />
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!displayData}>
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export
          </Button>
        </div>
      </motion.div>

      {/* Stat Cards — 6 across on xl, 3 on sm, 2 on mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        <StatCard title="Page Views"       value={overview?.totalPageViews}    icon={Eye}       subtitle={`last ${dateRange}`}     index={0} isFetching={isFetching} isLoading={isLoading} />
        <StatCard title="Unique Visitors"  value={overview?.uniqueVisitors}    icon={Users}     subtitle={`last ${dateRange}`}     index={1} isFetching={isFetching} isLoading={isLoading} />
        <StatCard title="Published Sites"  value={overview?.publishedSites}    icon={Globe}     subtitle="live on the web"         index={2} isFetching={isFetching} isLoading={isLoading} />
        <StatCard title="Total Clients"    value={overview?.totalClients}      icon={Activity}  subtitle="registered accounts"     index={3} isFetching={isFetching} isLoading={isLoading} />
        <StatCard title="Edits"            value={overview?.editsInPeriod}     icon={FileEdit}  subtitle={`last ${dateRange}`}     index={4} isFetching={isFetching} isLoading={isLoading} />
        <StatCard title="Publishes"        value={overview?.publishesInPeriod} icon={Rocket}    subtitle={`last ${dateRange}`}     index={5} isFetching={isFetching} isLoading={isLoading} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Activity Over Time — 2/3 width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="lg:col-span-2"
        >
          <Card className="border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-dark-200 h-full">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-accent-600" />
                <CardTitle className="text-base">Platform Activity</CardTitle>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Edits, publishes and page views over time</p>
            </CardHeader>
            <CardContent>
              <ActivityChart data={activityData} />
            </CardContent>
          </Card>
        </motion.div>

        {/* Device Breakdown / Sites by Status — 1/3 width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <Card className="border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-dark-200 h-full">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-accent-600" />
                <CardTitle className="text-base">{deviceTypes.length > 0 ? 'Device Breakdown' : 'Sites by Status'}</CardTitle>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {deviceTypes.length > 0 ? `${(overview?.totalPageViews ?? 0).toLocaleString()} total visits` : `${overview?.totalSites ?? 0} total sites`}
              </p>
            </CardHeader>
            <CardContent>
              <DeviceOrStatusChart deviceTypes={deviceTypes} published={overview?.publishedSites ?? 0} draft={overview?.draftSites ?? 0} />
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Traffic Row — Sources + Per-site (shown when visit data exists) */}
      {(referrerData.length > 0 || siteTraffic.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Traffic Sources */}
          {referrerData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.45 }}
            >
              <Card className="border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-dark-200 h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-accent-600" />
                    <CardTitle className="text-base">Traffic Sources</CardTitle>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Where visitors are coming from</p>
                </CardHeader>
                <CardContent className="space-y-3 pt-2">
                  {referrerData.map((src, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: src.color }} />
                          <span className="font-medium text-neutral-900 dark:text-neutral-100">{src.source}</span>
                        </div>
                        <div className="flex items-center gap-3 text-right">
                          <span className="text-neutral-500 dark:text-neutral-400 text-xs">{src.count.toLocaleString()} visits</span>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100 w-10 text-right">{src.percentage}%</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${src.percentage}%` }}
                          transition={{ duration: 0.6, delay: 0.5 + i * 0.1 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: src.color }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Traffic by Site */}
          {siteTraffic.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <Card className="border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-dark-200 h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-accent-600" />
                    <CardTitle className="text-base">Traffic by Site</CardTitle>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Most visited published sites</p>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[360px]">
                      <thead>
                        <tr className="border-b border-neutral-100 dark:border-neutral-700">
                          <th className="text-left py-2.5 px-6 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Site</th>
                          <th className="text-right py-2.5 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Views</th>
                          <th className="text-right py-2.5 px-6 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Unique</th>
                        </tr>
                      </thead>
                      <tbody>
                        {siteTraffic.map((site, i) => (
                          <tr key={i} className="border-b border-neutral-50 dark:border-neutral-700/50 hover:bg-neutral-50 dark:hover:bg-dark-300 transition-colors">
                            <td className="py-3 px-6">
                              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate max-w-[160px]">{site.name}</p>
                              <p className="text-xs text-neutral-400 dark:text-neutral-500 truncate max-w-[160px]">{site.company_name}</p>
                            </td>
                            <td className="py-3 px-4 text-right text-sm font-semibold text-neutral-900 dark:text-neutral-100">{site.views.toLocaleString()}</td>
                            <td className="py-3 px-6 text-right text-sm text-neutral-500 dark:text-neutral-400">{site.unique_visitors.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      )}

      {/* Tables Row — Most Active + Recently Published */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Most Active Sites */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.55 }}
        >
          <Card className="border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-dark-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <FileEdit className="h-4 w-4 text-accent-600" />
                <CardTitle className="text-base">Most Active Sites</CardTitle>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">By edit count in period</p>
            </CardHeader>
            <CardContent className="p-0">
              {activeSites.length === 0 ? (
                <p className="text-sm text-neutral-400 dark:text-neutral-500 px-6 py-8 text-center">No activity recorded yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[360px]">
                    <thead>
                      <tr className="border-b border-neutral-100 dark:border-neutral-700">
                        <th className="text-left py-2.5 px-6 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Site</th>
                        <th className="text-left py-2.5 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Client</th>
                        <th className="text-right py-2.5 px-6 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Edits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSites.map((site, i) => (
                        <tr key={i} className="border-b border-neutral-50 dark:border-neutral-700/50 hover:bg-neutral-50 dark:hover:bg-dark-300 transition-colors">
                          <td className="py-3 px-6 text-sm font-medium text-neutral-900 dark:text-neutral-100 max-w-[140px] truncate">{site.name}</td>
                          <td className="py-3 px-4 text-sm text-neutral-500 dark:text-neutral-400 max-w-[120px] truncate">{site.company_name}</td>
                          <td className="py-3 px-6 text-right">
                            <span className="inline-block bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 text-xs font-semibold px-2 py-0.5 rounded-full">
                              {site.editCount}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recently Published */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
        >
          <Card className="border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-dark-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Rocket className="h-4 w-4 text-accent-600" />
                <CardTitle className="text-base">Recently Published</CardTitle>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Sites published in this period</p>
            </CardHeader>
            <CardContent className="p-0">
              {recentPublishes.length === 0 ? (
                <p className="text-sm text-neutral-400 dark:text-neutral-500 px-6 py-8 text-center">No publishes in this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[360px]">
                    <thead>
                      <tr className="border-b border-neutral-100 dark:border-neutral-700">
                        <th className="text-left py-2.5 px-6 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Site</th>
                        <th className="text-left py-2.5 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Client</th>
                        <th className="text-right py-2.5 px-6 text-xs font-semibold text-neutral-500 dark:text-neutral-400">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentPublishes.map((site, i) => (
                        <tr key={i} className="border-b border-neutral-50 dark:border-neutral-700/50 hover:bg-neutral-50 dark:hover:bg-dark-300 transition-colors">
                          <td className="py-3 px-6 text-sm font-medium text-neutral-900 dark:text-neutral-100 max-w-[140px] truncate">{site.name}</td>
                          <td className="py-3 px-4 text-sm text-neutral-500 dark:text-neutral-400 max-w-[120px] truncate">{site.company_name}</td>
                          <td className="py-3 px-6 text-right text-xs text-neutral-400 dark:text-neutral-500 whitespace-nowrap">
                            {new Date(site.published_at).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Top Pages */}
      {topPages.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.65 }}
        >
          <Card className="border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-dark-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-accent-600" />
                <CardTitle className="text-base">Top Pages</CardTitle>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Most visited pages in the selected period</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px]">
                  <thead>
                    <tr className="border-b border-neutral-100 dark:border-neutral-700">
                      <th className="text-left py-2.5 px-6 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Page</th>
                      <th className="text-right py-2.5 px-4 text-xs font-semibold text-neutral-500 dark:text-neutral-400">Views</th>
                      <th className="text-right py-2.5 px-6 text-xs font-semibold text-neutral-500 dark:text-neutral-400">% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPages.map((page, i) => (
                      <tr key={i} className="border-b border-neutral-50 dark:border-neutral-700/50 hover:bg-neutral-50 dark:hover:bg-dark-300 transition-colors">
                        <td className="py-3 px-6 text-sm font-mono text-neutral-700 dark:text-neutral-300 max-w-[200px] truncate">{page.page}</td>
                        <td className="py-3 px-4 text-right text-sm font-semibold text-neutral-900 dark:text-neutral-100 whitespace-nowrap">{page.views.toLocaleString()}</td>
                        <td className="py-3 px-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 bg-neutral-100 dark:bg-neutral-700 rounded-full h-1.5 overflow-hidden hidden sm:block">
                              <div className="h-full bg-accent-600 rounded-full" style={{ width: `${page.percentage}%` }} />
                            </div>
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 w-10 text-right">{page.percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

    </div>
  )
}

export default AnalyticsDashboard
